/**
 * Feature catalog - the single source of truth for AI features that can be
 * toggled per user/org via the admin "Feature access" controls.
 *
 * Each AI feature has a stable `key` (persisted in User.featureAccess.hiddenAiFeatures),
 * a human `label`, a short `description`, and the roles it is relevant for (so the
 * admin picker only shows features that apply to the account being edited).
 *
 * To gate a new AI surface:
 *   1. Add an entry here with a new stable key.
 *   2. Wrap the UI with `isAiFeatureEnabled(user, '<key>')` from
 *      client/src/utils/featureAccess.js.
 */

export const AI_FEATURES = [
    {
        key: 'assistant',
        label: 'AI Assistant / Chat',
        description: 'The floating "Ask IPM" chat assistant and how-to helper.',
        roles: null, // null → applies to every role
    },
    {
        key: 'listing_description',
        label: 'AI Listing Descriptions',
        description: 'Auto-generate marketing copy when creating or editing a listing.',
        roles: ['agent', 'independent_agent', 'agency_agent', 'agency', 'enterprise'],
    },
    {
        key: 'voice_listing',
        label: 'Voice-to-Listing Draft',
        description: 'Dictate a listing and have AI draft the fields.',
        roles: ['agent', 'independent_agent', 'agency_agent', 'agency'],
    },
    {
        key: 'photo_ai',
        label: 'AI Photo Analysis',
        description: 'Analyse listing photos with AI to suggest features, tags and highlights.',
        roles: ['agent', 'independent_agent', 'agency_agent', 'agency', 'enterprise'],
    },
    {
        key: 'cma_intelligence',
        label: 'AI CMA Intelligence',
        description: 'AI-generated commentary and pricing insight in Seller CMA reports.',
        roles: ['agent', 'independent_agent', 'agency_agent', 'agency', 'enterprise'],
    },
    {
        key: 'lead_matching',
        label: 'AI Lead / Property Matching',
        description: 'AI match scores and top-match suggestions in the CRM.',
        roles: ['agent', 'independent_agent', 'agency_agent', 'agency', 'enterprise'],
    },
    {
        key: 'marketing_content',
        label: 'AI Marketing Content',
        description: 'AI-generated social / campaign content in the Marketing tools.',
        roles: ['agent', 'independent_agent', 'agency_agent', 'agency', 'enterprise'],
    },
    {
        key: 'comms_replies',
        label: 'AI Suggested Replies',
        description: 'AI-drafted replies in the messaging / comms panel.',
        roles: null,
    },
    {
        key: 'market_insights',
        label: 'AI Market Insights',
        description: 'AI-written market trend interpretations on the dashboard.',
        roles: null,
    },
];

/** All AI feature keys (useful for validation on the server/admin). */
export const AI_FEATURE_KEYS = AI_FEATURES.map((f) => f.key);

/** AI features relevant to a given role (for the admin picker). */
export function aiFeaturesForRole(role) {
    const r = String(role || '').toLowerCase();
    return AI_FEATURES.filter((f) => !f.roles || f.roles.includes(r));
}
